/*
 * <frequently-bought-together> — cart drawer recommendations carousel.
 *
 * Fetches Shopify product recommendations ("related" intent) for the most
 * recently added product, renders them as a Swiper carousel, and adds them to
 * the cart using the theme's standard cart-drawer flow (matches product-form.js).
 *
 * Data attributes:
 *   data-product-id   → product id to base recommendations on (most recent add)
 *   data-exclude-ids  → comma-separated product ids already in the cart
 *   data-limit        → max number of products to show
 *   data-money-format → shop money format string (for price rendering)
 */
if (!customElements.get('frequently-bought-together')) {
  customElements.define(
    'frequently-bought-together',
    class FrequentlyBoughtTogether extends HTMLElement {
      constructor() {
        super();
        this.swiper = null;
      }

      connectedCallback() {
        this.productId = this.dataset.productId;
        this.limit = parseInt(this.dataset.limit || '8', 10);
        this.excludeIds = (this.dataset.excludeIds || '').split(',').filter(Boolean);
        this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
        this.track = this.querySelector('[data-fbt-track]');
        this.scrollbarEl = this.querySelector('[data-fbt-scrollbar]');

        if (!this.productId || !this.track) {
          this.hidden = true;
          return;
        }
        this.loadRecommendations();
      }

      disconnectedCallback() {
        if (this.swiper) this.swiper.destroy(true, true);
      }

      get recommendationsUrl() {
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const count = this.limit + this.excludeIds.length;
        return `${root}recommendations/products.json?product_id=${this.productId}&intent=related&limit=${count}`;
      }

      async loadRecommendations() {
        try {
          const response = await fetch(this.recommendationsUrl);
          const data = await response.json();
          const products = (data.products || [])
            .filter((product) => this.excludeIds.indexOf(String(product.id)) === -1)
            .slice(0, this.limit);

          if (!products.length) {
            this.hidden = true;
            return;
          }

          this.track.innerHTML = products.map((product) => this.cardMarkup(product)).join('');
          this.hidden = false;
          this.initSwiper();
          this.bindAddButtons();
        } catch (error) {
          console.error('[frequently-bought-together]', error);
          this.hidden = true;
        }
      }

      cardMarkup(product) {
        const variant = (product.variants || []).find((v) => v.available) || (product.variants || [])[0];
        const image = product.featured_image || (product.images && product.images[0]);
        const available = variant && variant.available;
        const price = this.formatMoney(variant ? variant.price : product.price);

        return `
          <div class="swiper-slide fbt__slide">
            <div class="fbt__card">
              <a href="${product.url}" class="fbt__media" tabindex="-1" aria-hidden="true">
                ${image ? `<img src="${image}" alt="${this.escape(product.title)}" loading="lazy" width="112" height="112">` : ''}
              </a>
              <div class="fbt__body">
                <div class="fbt__text">
                  <a href="${product.url}" class="fbt__name">${this.escape(product.title)}</a>
                  <span class="fbt__price">${price}</span>
                </div>
                <button
                  type="button"
                  class="fbt__add"
                  data-variant-id="${variant ? variant.id : ''}"
                  ${available ? '' : 'disabled'}
                >
                  ${available ? 'ADD' : 'SOLD OUT'}
                </button>
              </div>
            </div>
          </div>`;
      }

      initSwiper() {
        if (typeof Swiper === 'undefined') return;
        this.swiper = new Swiper(this.querySelector('[data-fbt-swiper]'), {
          slidesPerView: 'auto',
          spaceBetween: 32,
          freeMode: true,
          grabCursor: true,
          scrollbar: this.scrollbarEl ? { el: this.scrollbarEl, draggable: true } : false,
        });
      }

      bindAddButtons() {
        this.querySelectorAll('.fbt__add').forEach((button) =>
          button.addEventListener('click', () => this.addToCart(button))
        );
      }

      async addToCart(button) {
        const variantId = button.dataset.variantId;
        if (!variantId || button.classList.contains('loading') || button.hasAttribute('disabled')) return;

        const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');

        button.classList.add('loading');
        button.setAttribute('aria-disabled', 'true');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', 1);

        if (cart && typeof cart.getSectionsToRender === 'function') {
          formData.append('sections', cart.getSectionsToRender().map((section) => section.id).join(','));
          formData.append('sections_url', window.location.pathname);
          if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        try {
          const response = await fetch(`${routes.cart_add_url}`, config).then((res) => res.json());

          if (response.status) {
            console.error('[frequently-bought-together]', response.description || response.message);
            return;
          }

          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'frequently-bought-together',
            productVariantId: variantId,
            cartData: response,
          });

          if (cart && typeof cart.renderContents === 'function') {
            cart.classList.remove('is-empty');
            cart.renderContents(response);
          }
        } catch (error) {
          console.error('[frequently-bought-together]', error);
        } finally {
          button.classList.remove('loading');
          button.removeAttribute('aria-disabled');
        }
      }

      /* Classic Shopify money formatter (handles the shop's money_format string). */
      formatMoney(cents) {
        if (typeof cents === 'string') cents = cents.replace('.', '');
        const format = this.moneyFormat;
        const placeholder = /\{\{\s*(\w+)\s*\}\}/;

        function defaultTo(value, fallback) {
          return value == null || isNaN(value) ? fallback : value;
        }

        function formatWithDelimiters(number, precision, thousands, decimal) {
          precision = defaultTo(precision, 2);
          thousands = defaultTo(thousands, ',');
          decimal = defaultTo(decimal, '.');

          if (isNaN(number) || number == null) return 0;
          number = (number / 100.0).toFixed(precision);

          const parts = number.split('.');
          const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
          const cents = parts[1] ? decimal + parts[1] : '';
          return dollars + cents;
        }

        let value = '';
        const match = format.match(placeholder);
        const token = match ? match[1] : 'amount';

        switch (token) {
          case 'amount':
            value = formatWithDelimiters(cents, 2);
            break;
          case 'amount_no_decimals':
            value = formatWithDelimiters(cents, 0);
            break;
          case 'amount_with_comma_separator':
            value = formatWithDelimiters(cents, 2, '.', ',');
            break;
          case 'amount_no_decimals_with_comma_separator':
            value = formatWithDelimiters(cents, 0, '.', ',');
            break;
          case 'amount_with_space_separator':
            value = formatWithDelimiters(cents, 2, ' ', ',');
            break;
          default:
            value = formatWithDelimiters(cents, 2);
        }

        return format.replace(placeholder, value);
      }

      escape(string) {
        return String(string == null ? '' : string)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      }
    }
  );
}
